<template>
  <BaseChartPanel
    :title="t('importance_stat_bar.title')"
    :date="date"
    :chart-height="stylesChart.heightStackedHorizontalBarChart"
    :chart-key="`${refreshCounter}imp`"
    :chart-options="chartOptions"
    :series="(newSeries.length ? newSeries : series) || []"
    :loading="loading"
    @refresh="loadData"
    @update:date="date = $event"
  >
    <template #controls>
      <ChartFilterDropdown
        v-model:chart-type="selectedChart"
        :active-store="activeStore"
        :date="date"
        all-enabled
        @clear="clearRubrics"
      />
    </template>
  </BaseChartPanel>
</template>

<script setup>
import { computed, ref, reactive, watch } from "vue";
import { useImportanceStatBlocksStore } from "@/store/Analytic/chart/importance-stat-dynamic-blocks";
import { useImportanceByRubricStatBlocksStore } from "@/store/Analytic/chart/importance-by-rubric-stat-dynamic-blocks";
import { stylesChart } from "@/tools/tooltipChart";
import { t } from "@/controllerLK/AvalLocalization";
import useFilterDate from "@/mixins/apexChart/filterDate";
import ChartFilterDropdown from "./blocks/ChartFilterDropdown.vue";
import useColumnChart from "@/mixins/apexChart/columnChart";
import { importanceList } from "@/const/importance.list";
import BaseChartPanel from "./BaseChartPanel.vue";

const statStorage = useImportanceStatBlocksStore();
const statRubricStorage = useImportanceByRubricStatBlocksStore();

const { date } = useFilterDate(loadData);
const loading = ref(true);
const selectedChart = ref(0);
const newSeries = ref([]);
const refreshCounter = ref(0);

async function loadData() {
  await statStorage.loadStat(date.value[0], date.value[1], [], true);
  await statRubricStorage.loadStat(date.value[0], date.value[1], [], true);
  loading.value = false;
}

const activeStore = computed(() => {
  return selectedChart.value === 1 ? statRubricStorage : statStorage;
});

const categoriesForAPI = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const categoriesForUsers = [
  t("aval_calendar.day_short.monday"),
  t("aval_calendar.day_short.tuesday"),
  t("aval_calendar.day_short.wednesday"),
  t("aval_calendar.day_short.thursday"),
  t("aval_calendar.day_short.friday"),
  t("aval_calendar.day_short.saturday"),
  t("aval_calendar.day_short.sunday")
];

function clearRubrics() {
  loading.value = true;
  statStorage.items = [];
  statRubricStorage.items = [];
  newSeries.value = [];
  chartOptions.value = useColumnChart(
    {
      noDataText: t("common_chart.no_data.short_text"),
      titleText: t("importance_stat_bar.title"),
      stacked: false,
    },
    categoriesForUsers,
    []
  ).chartOptions;
  refreshCounter.value++;
  setTimeout(() => {
    loading.value = false;
  }, 0);
}

function aggregateByDay(data) {
  loading.value = true;
  const resultObj = {};
  for (const day of categoriesForAPI) {
    resultObj[day] = {};
  }

  for (const entry of data) {
    const { day, countByImportance } = entry;
    if (!resultObj[day]) resultObj[day] = {};

    for (const key in countByImportance) {
      if (!resultObj[day][key]) {
        resultObj[day][key] = 0;
      }
      resultObj[day][key] += countByImportance[key];
    }
  }

  // Преобразуем в массив объектов
  const resByDays = categoriesForAPI.map(day => ({
    name: day,
    data: resultObj[day],
  }));

  // Все возможные ключи, в нужном порядке
  const keys = ["4", "3", "2", "1"];

  const result = Object.values(importanceList).filter((importance) => {
    return importance.importance !== null && importance.importance !== 0;
  }).toReversed().map((item) => {
    return {
      name: t(`importance_stat_bar.importance.${item.name}`),
      color: item.analyticColor,
      data: []
    };
  });

  keys.forEach((key, idx) => {
    for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
      const dayData = resByDays[dayIndex].data;
      const value = key in dayData ? dayData[key] : 0;
      result[idx].data.push(value);
    }
    if (result[idx].data.every((item) => item === 0)) {
      result[idx].data = []; // Если приходят пустые объекты, то не отображаем пустой график, а отображаем "По вашему запросу ничего не найдено"
    }
  });
  loading.value = false;
  return result;
}

watch(
  () => selectedChart.value,
  () => {
    loading.value = true;
    setTimeout(() => {
      chartOptions.value = useColumnChart(
        {
          titleText: t("importance_stat_bar.title"),
          stacked: false,
        },
        categoriesForUsers || [],
        series.value || []
      ).chartOptions;
      setTimeout(() => loading.value = false, 0);
    }, 0);
  }
);

const series = computed(() => aggregateByDay(activeStore?.value?.items || []));

const chartOptions = reactive(useColumnChart(
  {
    titleText: t("importance_stat_bar.title"),
    stacked: false,
  },
  categoriesForUsers,
  series.value || []
).chartOptions);
</script>
